import { useContext, createContext, ReactNode, useCallback, useState, useMemo } from 'react';
import { FileType } from 'shared/enums/FileType';
import { useEditProfilePhotoEditor } from './EditProfilePhotoEditorV2Provider';
import { EPhotoEditorConsumer } from 'shared/enums/EPhotoEditorConsumer';
import { useEditProfileMedia } from './EditProfileMediaProvider';
import { useTheme } from '@material-ui/core';
import { AgencyPermissionEnums } from 'shared/enums/AgencyPermissionEnums';
import AsyncFileDropZone from 'components/AsyncFileDropZone/AsyncFileDropZone';
import DialogInfo from 'components/DialogInfo/DialogInfo';
import { isValidFileSize } from 'shared/utils/isValidFileSize';
import { MaxSizeFileType } from 'shared/constants/MAX_SIZE';
import { useRepresentativesModal } from 'pages/Representatives/context/RepresentativesModalProvider';

type EditProfileMediaDocumentsProviderContextProps = {
  handleSetAddModal: (fileType: FileType, onUpload?: (files: File[]) => void) => void;
};

export const EditProfileMediaDocumentsProviderContext = createContext<EditProfileMediaDocumentsProviderContextProps>({
  handleSetAddModal: () => {},
});

// Map file types to permission enums
const fileTypePermissionMap: any = {
  [FileType.IMAGE]: AgencyPermissionEnums.IMAGES,
  [FileType.VIDEO]: AgencyPermissionEnums.VIDEOS,
  [FileType.AUDIO]: AgencyPermissionEnums.AUDIO_FILES,
  [FileType.DOCUMENT]: AgencyPermissionEnums.DOCUMENTS,
};

// Optional fileTypes mapping for drop zone, adjust as needed
const types: any = {
  [FileType.IMAGE]: 'images',
  [FileType.VIDEO]: 'videos',
  [FileType.AUDIO]: 'audios',
  [FileType.DOCUMENT]: 'documents',
};

export const EditProfileMediaDocumentsProvider = ({ children }: { children: ReactNode }) => {
  const theme = useTheme();
  const { handleOpen, handleSetMessage } = useRepresentativesModal();
  const { handlePhotoEditorOpen } = useEditProfilePhotoEditor();
  const { handleAction } = useEditProfileMedia();

  const [openAddModal, setAddModal] = useState<boolean>(false);
  const [fileType, setFileType] = useState<FileType | null>(null);
  const [onUploadComplete, setOnUploadComplete] = useState<((files: File[]) => void) | null>(null);

  const handleSetAddModal = useCallback((fileType: FileType, onUpload?: (files: File[]) => void) => {
    const permission = fileTypePermissionMap[fileType];
    handleAction(permission, () => {
      setFileType(fileType);
      setOnUploadComplete(() => onUpload);
      setAddModal(true);
    });
  }, []);

  const handleCloseModal = useCallback(() => {
    setFileType(null);
    setOnUploadComplete(null);
    setAddModal(false);
  }, []);

  console.log(types[fileType || FileType.DOCUMENT]);

  const value = useMemo(() => ({ handleSetAddModal }), [handleSetAddModal]);

  return (
    <EditProfileMediaDocumentsProviderContext.Provider value={value}>
      {openAddModal && fileType ? (
        <DialogInfo openDialog={openAddModal} handleDialog={handleCloseModal} title={`Add ${fileType}`}>
          <div style={{ height: 300 }}>
            <AsyncFileDropZone
              fileTypes={types[fileType]}
              hideNote={true}
              getUploadedFiles={(uploadedFiles: File[]) => {
                let file_mb = '';
                const type: MaxSizeFileType = types[fileType].slice(0, -1);
                const validFiles = uploadedFiles.filter((file) => {
                  const { isValid, size_message } = isValidFileSize(type, file);
                  if (!isValid && file_mb === '') file_mb = size_message;
                  return isValid;
                });
                if (fileType === FileType.IMAGE) {
                  handlePhotoEditorOpen(validFiles, EPhotoEditorConsumer.PROFILE_MEDIA_IMAGE);
                }

                if (onUploadComplete) {
                  onUploadComplete(validFiles);
                }

                // If not the same, it means there are invalid files
                if (uploadedFiles?.length !== validFiles?.length) {
                  handleSetMessage(
                    'Limit Exceeded',
                    `Some ${fileType} you’re trying to upload exceeds our ${file_mb} file size limit. We recommend you to either trim down or compress this file.`,
                    'Close',
                  );
                  handleOpen();
                  file_mb = '';
                }

                handleCloseModal();
              }}
              theme={theme.palette.type}
            />
          </div>
        </DialogInfo>
      ) : null}
      {children}
    </EditProfileMediaDocumentsProviderContext.Provider>
  );
};

export const useEditProfileMediaDocuments = () => {
  const context = useContext(EditProfileMediaDocumentsProviderContext);

  if (context === undefined) {
    throw new Error('You need context for this component');
  }

  return context;
};
